/* Data --------------------------------------------------------------- */

import { ICookiesPopupProps } from '../components/common/CookiesPopUp';
import { IFooterProps } from '../components/common/layout/Footer';
import { INavBarProps } from '../components/common/layout/NavBar';
import { PageJSONLDSchema } from './app.models';

export interface IStrapiCollectionResult<T> {
  data: IStrapiData<T>[];
  meta?: IStrapiMeta;
}

export interface IStrapiData<T> {
  id?: number;
  attributes: T;
}

export interface IStrapiMeta {
  pagination?: IPagination;
}

export interface IPagination {
  page?: number;
  pageSize?: number;
  pageCount?: number;
  total?: number;
}

export interface IBlogPostsResult {
  results: IBlogPost[];
  pagination: IPagination;
}

export interface IBlogPost {
  id?: number;
  title: string;
  body: string;
  author?: ITeamMemberPage;
  metaDescription?: string;
  metaKeywords?: string;
  slug?: string;
  publicationDate?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  publishedAt?: Date;
  locale?: string;
  image?: IMedia;
  createdBy?: IUser;
  updatedBy?: IUser;
  localizations?: ILocalization[];
  isFeatured?: boolean;
  breadcrumbAncestors?: IBreadcrumb[];
  jsonldAddiontalSchema?: string;
  bodyTitle?: string;
  summary?: string;
  callToAction?: ICallToActionComponent;
}

export interface IUser {
  id?: number;
  username?: null;
  email?: string;
  password?: string;
  resetPasswordToken?: null;
  registrationToken?: null;
  isActive?: boolean;
  blocked?: boolean;
  preferedLanguage?: null;
  createdAt?: Date;
  updatedAt?: Date;
  user_info?: IUserInfo;
}

export interface IMe {
  id?: number;
  email: string;
  user_info: IUserInfo;
  requestIp: string;
  countryCode: string; // ISO3
}

export interface IHeaders {
  accept: string;
  authorization: string;
  'user-agent': string;
  'cache-control': string;
  'postman-token': string;
  host: string;
  'accept-encoding': string;
  connection: string;
}

export interface IUserInfo {
  id?: number;
  name: string;
  newsletter: boolean;
  phone: string;
  createdAt: Date;
  updatedAt: Date;
  currency: ICurrency;
  assignedConsultant: IUser;
  language: ILanguage;
}

export interface IMediaFormats {
  thumbnail?: IMediaFormat;
  small?: IMediaFormat;
  large?: IMediaFormat;
  medium?: IMediaFormat;
  xlarge?: IMediaFormat;
}

export interface IMediaFormat {
  name?: string;
  hash?: string;
  ext?: string;
  mime?: string;
  path?: null;
  width?: number;
  height?: number;
  size?: number;
  url?: string;
}

export interface ILocalization {
  id?: number;
  title?: string;
  body?: string;
  author?: string;
  metaDescription?: string;
  slug?: string;
  publicationDate?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  publishedAt?: Date;
  locale?: string;
}

export interface ILanguage {
  id?: number;
  twoLetterIso?: string;
  threeLetterIso?: string;
  nameTranslations?: ISimpleTranslation[];
}

export interface ICountry {
  id?: number;
  iso3: string;
  iso: string;
  internalName: string;
  numericIso: number;
  createdAt: Date;
  updatedAt: Date;
  dividesByClass: boolean;
  dateFromWhichValidityIsCalculated: string;
  estimatedProcessingMonths: number;
  yearsOfValidity: number;
  currency: ICurrency;
  nameTranslations: ISimpleTranslation[];
  searchClassPrices: IApplicationClassPrice[];
  applicationClassPrices: IApplicationClassPrice[];
  finalFees: IApplicationClassPrice[];
  requiredDocuments?: ISimpleTranslation[];
  publicPriceNotes?: ISimpleTranslation[];
}

export interface ICountryRegion {
  nameTranslations: ISimpleTranslation[];
  countries: ICountry[];
  featuredCountries?: ICountry[];
  searchCountries?: ICountry[];
  featuredSearchCountries?: ICountry[];
  copyrightCountries?: ICountry[];
  trademarkRenewalCountries?: ICountry[];
  industrialDesignCountries?: ICountry[];
  featuredCopyrightCountries?: ICountry[];
  featuredTrademarkRenewalCountries?: ICountry[];
  featuredIndustrialDesignCountries?: ICountry[];
  declarationOfUseCountries?: ICountry[];
  featuredDeclarationOfUseCountries?: ICountry[];
  order: number;
}

export interface ICurrency {
  id?: number;
  isoCode?: string;
  numericIso?: number;
  numberOfDecimals?: number;
  internalName?: string;
  symbol?: string;
  conversionRate?: number;
  nameTranslations?: ISimpleTranslation[];
}

export interface ISubClass {
  subClassId: number;
  descriptionTranslations: ISimpleTranslation[];
}

export interface IClass {
  id?: number;
  code: number;
  descriptionTranslations: ISimpleTranslation[];
  subClasses?: ISubClass[];
}

export interface IApplicationClassPrice {
  id?: number;
  value: number;
  valueName?: string;
}

export interface ISimpleTranslation {
  id?: number;
  language?: string;
  text?: string;
}

export interface IImageTranslation {
  id?: number;
  language?: string;
  image: IMedia;
}

export interface IFrequentlyAskedQuestion {
  id?: number;
  language?: string;
  question?: string;
  answer?: string;
}

export interface IBreadcrumb {
  id?: number;
  language?: string;
  label?: string;
  path?: string;
}

export interface IMedia {
  id?: number;
  name?: string;
  alternativeText?: null;
  caption?: null;
  width?: number;
  height?: number;
  formats?: IMediaFormats;
  hash?: string;
  ext?: string;
  mime?: string;
  size?: number;
  url?: string;
  previewUrl?: null;
  provider?: string;
  provider_metadata?: IMediaProviderMetadata;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IMediaProviderMetadata {
  public_id?: string;
  resource_type?: string;
}

export interface ILocale {
  id?: number;
  name?: string;
  code?: string;
  createdAt?: Date;
  updatedAt?: Date;
  isDefault?: boolean;
}

export interface IContactMethod {
  id?: number;
  labelTranslations: ISimpleTranslation[];
  valueTranslations?: ISimpleTranslation[];
  type: 'phone' | 'email';
}

export interface ICustomerReview {
  id?: number;
  content: string;
  customerName: string;
  score: number;
  date: Date;
  link?: string;
}

export interface ICheckoutCustomerReview {
  content: string;
  score: number;
  date: Date;
  user: number;
}

export enum OrderStatus {
  COMPLETED = 'completed',
  CANCELED = 'canceled',
  DRAFT = 'draft',
  PENDING = 'pending',
  PAID_AND_PENDING_TRACKER_CREATION = 'paidAndPendingTrackersCreation',
  PAID_BUTTON_ERROR_CREATING_TRACKERS = 'paidButErrorCreatingTrackers',
}

export interface IOrder {
  id?: number;
  user?: number | IUser;
  billingAddress?: string;
  billingCity?: string;
  billingName?: string;
  billingPostcode?: string;
  billingTitle?: string;
  billingTaxId?: string;
  billingCountry?: number | ICountry;
  currency?: number | ICurrency;
  totalPrice?: number;
  status: OrderStatus;
  payment?: number | IPayment;
  searchTrackers?: ISearchTracker[];
  trademarkTrackers?: ITrademarkTracker[];
  orderItems?: IOrderItem[];
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface IPayment {
  orders?: number[] | IOrder[];
  type?: 'card' | 'bankwire' | 'other' | 'attorney_services';
  status: 'pending' | 'completed' | 'refunded';
  currency?: number | ICurrency;
  paid: number;
  paymentInternalId: string;
  createdByAdmin?: boolean;
  lastInvoice?: IMedia;
  previousInvoices?: IMedia[];
  paymentConfirmationEmailSent?: boolean;
  used?: number;
  balance?: number;
  user?: IUser | number;
}

export enum TrackerStatus {
  IN_PROCESS = 'in_process',
  REGISTERED = 'registered',
  FILED = 'filed',
  COMPLETED = 'completed',
  INACTIVE = 'inactive',
  ABANDONED = 'abandoned',
  REFUSED = 'refused',
  WAITIN_FOR_POA = 'waiting_for_poa',
}

export interface ITrackerFile {
  file: IMedia;
  description?: string;
}

export interface ITrademarkTracker {
  id?: number;
  trackerTitle: string;
  trademarkText: string;
  user?: number | IUser;
  country?: number | ICountry;
  logo?: number | IMedia;
  classes?: { class: IClass; description?: string }[];
  ownerAddress?: string;
  ownerCity?: string;
  ownerCountry?: number | ICountry;
  ownerPostCode?: string;
  ownerName?: string;
  ownerAddressAlias?: string;
  attorney?: number | IUser;
  status: TrackerStatus;
  filingNumber?: string;
  filingDate: string | Date;
  registrationNumber?: string;
  registrationDate?: string | Date;
  renewalDate?: string | Date;
  deadlineDate?: string | Date;
  order?: number | IOrder;
  files?: ITrackerFile[];
}

export interface ICopyrightTracker {
  id: number;
  trackerTitle?: string;
  trackerDescription?: string;
  country?: number | ICountry | null;
  ownerName?: string;
  ownerAddress?: string;
  ownerCity?: string;
  ownerPostCode?: string;
  status?: TrackerStatus;
  filingNumber?: string;
  filingDate?: string;
  registrationNumber?: string;
  registrationDate?: string;
  files?: ITrackerFile[];
}

export interface IDesignTracker extends ICopyrightTracker {
  renewalDate?: string;
}

export interface ISearchTracker {
  id?: number;
  trackerTitle: string;
  trademarkText: string;
  user?: number | IUser;
  country?: number | ICountry;
  logo?: number | IMedia;
  classes?: { class: IClass; description?: string }[];
  ownerAddress?: string;
  ownerCity?: string;
  ownerCountry?: number | ICountry;
  ownerPostCode?: string;
  ownerName?: string;
  ownerAddressAlias?: string;
  attorney?: number | IUser;
  status: TrackerStatus;
  uploadDate?: string | Date;
  updatedAt?: string | Date;
  results?: number;
  order?: number | IOrder;
  files?: ITrackerFile[];
  report?: IMedia;
}

export enum ServiceType {
  SEARCH = 'search',
  APPLICATION = 'application',
  ADDTIONAL_SERVICE = 'additional_service',
  INDUSTRIAL_DESIGN = 'industrial_design',
  COPYRIGHT = 'copyright',
}

export interface ICountryPrice {
  price?: number;
  country?: ICountry | number;
}

export interface IOrderItem {
  id?: number;
  order?: number | IOrder;
  serviceType?: ServiceType;
  trademarkText?: string;
  logo?: IMedia | number;
  ownerAddress?: string;
  ownerCity?: string;
  ownerCountry?: number | ICountry;
  ownerPostCode?: string;
  ownerName?: string;
  ownerAddressAlias?: string;
  countries?: ICountry[] | number[];
  classes?: IOrderItemClass[] | number[] | any[];
  prices?: ICountryPrice[];
}

export interface IOrderItemClass {
  id?: number;
  class: IClass | number | any;
  description?: string;
}

export interface IAddress {
  id?: number;
  user?: number | IUser;
  alias?: string;
  name?: string;
  address?: string;
  postCode?: string;
  city?: string;
  country?: number | ICountry;
  taxId?: string;
}

export interface ICulture {
  countryCode?: string;
  currencyCode?: string;
}

export interface ILog404 {
  url: string;
  timestamp: Date;
  referrerUrl?: string;
  user?: IUser | number;
  userAgent?: string;
}

/* Data --------------------------------------------------------------- */

/* Pages  ------------------------------------------------------------- */
export interface IPage {
  meta: IMetaComponent[];
}

export interface IHomePage extends IPage {
  hero: IHeroComponent[];
  featuredServices: IHomeFeaturedServicesComponent[];
  quickAccessTitle: ISimpleTranslation[];
  quickAccessSubTitle: ISimpleTranslation[];
  quickAccessShowAllButton: ISimpleTranslation[];
  trustedBy: ITrustedByComponent[];
  customerReviews: ICustomerReviewsComponent[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage: ISimpleTranslation[];
  contactUsFormErrorMessage: ISimpleTranslation[];
  introductionTitle: ISimpleTranslation[];
  introductionDescription: ISimpleTranslation[];
  introductionImage: IMedia;
  businessKeyValues: IBusinessKeyValuesComponent[];
  howItWorksTitle: ISimpleTranslation[];
  howItWorks: ITextBlockWithIcon[];
  callToAction: ICallToActionComponent[];
  faqsTitle: ISimpleTranslation[];
  faqs: IFrequentlyAskedQuestion[];
  callToActionWithImage: ICallToActionComponent[];
}

export interface IBlogPage extends IPage {
  sortOrder: 'asc' | 'desc';
  sortByPropertyName: string;
  contactUsCallToAction: IContactUsCallToActionComponent[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage: ISimpleTranslation[];
  contactUsFormErrorMessage: ISimpleTranslation[];
  tableOfContentsTitle: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  authorPrefix: ISimpleTranslation[];
  publishedDatePrefix: ISimpleTranslation[];
}

export interface IAboutPage extends IPage {
  breadcrumbAncestors: IBreadcrumb[];
  hero: IHeroComponent[];
  topHighlightedTitle: IHighlightedTitleComponent[];
  introductionTitle: ISimpleTranslation[];
  introductionDescription: ISimpleTranslation[];
  introductionImage: IMedia;
  howItWorksTitle: ISimpleTranslation[];
  howItWorks: ITextBlockWithIcon[];
  trustedBy: ITrustedByComponent[];
  customerReviews: ICustomerReviewsComponent[];
  faqsTitle: ISimpleTranslation[];
  faqs: IFrequentlyAskedQuestion[];
  contactUsForm: IContactUsForm[];
  callToAction: ICallToActionComponent[];
  contactUsFormSuccessMessage: ISimpleTranslation[];
  contactUsFormErrorMessage: ISimpleTranslation[];
  businessKeyValues: IBusinessKeyValuesComponent[];
  teamMembersTitle: ISimpleTranslation[];
  teamMembersSubTitle: ISimpleTranslation[];
}

export interface ICookiesPolicyPage extends IPage {
  content: ISimpleTranslation[];
  anchorHtmlTagName?: string;
}

export interface IPrivacyPolicyPage extends IPage {
  content: ISimpleTranslation[];
  anchorHtmlTagName?: string;
}

export interface ITermsPage extends IPage {
  content: ISimpleTranslation[];
  anchorHtmlTagName?: string;
}

export interface IContactUsPage extends IPage {
  title: ISimpleTranslation[];
  subTitle: ISimpleTranslation[];
  formSuccessMessage: ISimpleTranslation[];
  formErrorMessage: ISimpleTranslation[];
  contactUsForm: IContactUsForm[];
}

export interface ICustomErrorPage404 extends IPage {
  heading: ISimpleTranslation[];
  formSuccessMessage: ISimpleTranslation[];
  formErrorMessage: ISimpleTranslation[];
  contactUsForm: IContactUsForm[];
}

export interface IPricingPage extends IPage {
  title: ISimpleTranslation[];
  subTitle: ISimpleTranslation[];
  countries: ICountry[];
  countryNotListedTitle: ISimpleTranslation[];
  countryNotListedSubTitle: ISimpleTranslation[];
  countryNotListedButtonLabel: ISimpleTranslation[];
  businessKeyValues: IBusinessKeyValuesComponent[];
  trademarkSearchLabel: ISimpleTranslation[];
  trademarkRegistrationLabel: ISimpleTranslation[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage: ISimpleTranslation[];
  contactUsFormErrorMessage: ISimpleTranslation[];
}

export interface ICountryListPage extends IPage {
  title: ISimpleTranslation[];
  subTitle: ISimpleTranslation[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage: ISimpleTranslation[];
  contactUsFormErrorMessage: ISimpleTranslation[];
  trademarkRegistrationServiceName: ISimpleTranslation[];
}

export interface ICountryListSearchPage extends IPage {
  title: ISimpleTranslation[];
  subTitle: ISimpleTranslation[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage: ISimpleTranslation[];
  contactUsFormErrorMessage: ISimpleTranslation[];
  trademarkSearchServiceName: ISimpleTranslation[];
  trademarkSearchFreeServiceName: ISimpleTranslation[];
  trademarkSearchFreeServicePriceLabel: ISimpleTranslation[];
}

export interface ICountryPage extends IPage {
  heroTipsTitle: ISimpleTranslation[];
  heroTipOne: ISimpleTranslation[];
  heroTipTwo: ISimpleTranslation[];
  heroTipThree: ISimpleTranslation[];
  businessKeyValues: IBusinessKeyValuesComponent[];
  trustedBy: ITrustedByComponent[];
  customerReviews: ICustomerReviewsComponent[];
  fromLabel: ISimpleTranslation[];
  orderNowLabel: ISimpleTranslation[];
  formSuccessMessage: ISimpleTranslation[];
  formErrorMessage: ISimpleTranslation[];
  contactUsForm: IContactUsForm[];
  relatedServicesTitle: ISimpleTranslation[];
  freeLabel: ISimpleTranslation[];
  freeForLabel: ISimpleTranslation[];
  startNowLabel: ISimpleTranslation[];
  topCallToActionRegistration: ICallToActionComponent[];
  topCallToActionSearch: ICallToActionComponent[];
  bottomCallToAction: ICallToActionComponent[];
  countryKeyFacts: ICountryKeyFacts[];
  countryKeyFactsRegistration: ICountryKeyFacts[];
  countryKeyFactsRenewals: ICountryKeyFacts[];
}

export interface ITrademarkLanding {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  country: ICountry;
  metas: IMetaComponent[];
  keywords: ISimpleTranslation[];
  trademarkHeaders: ISimpleTranslation[];
  landingPageSubTitles: ISimpleTranslation[];
  trademarkSearchTitles: ISimpleTranslation[];
  trademarkSearchTexts: ISimpleTranslation[];
  trademarkApplicationTitles: ISimpleTranslation[];
  trademarkApplicationTexts: ISimpleTranslation[];
  bodies: ISimpleTranslation[];
  faqsTitle?: ISimpleTranslation[];
  faqs?: IFrequentlyAskedQuestion[];
  breadcrumbAncestors?: IBreadcrumb[];
}

export interface ITrademarkRegistrationCountryLanding {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  country: ICountry;
  meta: IMetaComponent[];
  breadcrumbAncestors: IBreadcrumb[];
  pageTitle: ISimpleTranslation[];
  pageTitleHighlightedText?: ISimpleTranslation[];
  pageSubTitle: ISimpleTranslation[];
  orderNowCallToActionTitle: ISimpleTranslation[];
  orderNowCallToActionText: ISimpleTranslation[];
  howToCallToAction: ICallToActionComponent[];
  doItYourselfVsProfessionalTitle: ISimpleTranslation[];
  doItYourselfList: IServiceListComponent[];
  professionalList: IServiceListComponent[];
  customerReviews: ICustomerReviewsComponent[];
  faqsTitle: ISimpleTranslation[];
  faqs: IFrequentlyAskedQuestion[];
  topCallToAction?: ICallToActionComponent[];
  bottomCallToAction?: ICallToActionComponent[];
  countryKeyFacts?: ICountryKeyFacts[];
}

export interface ITrademarkSearchCountryLanding {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  country: ICountry;
  meta: IMetaComponent[];
  breadcrumbAncestors: IBreadcrumb[];
  pageTitle: ISimpleTranslation[];
  pageTitleHighlightedText?: ISimpleTranslation[];
  pageSubTitle: ISimpleTranslation[];
  orderForFreeCallToActionTitle: ISimpleTranslation[];
  orderForFreeCallToActionText: ISimpleTranslation[];
  orderForFreeCallToActionFooterText: ISimpleTranslation[];
  orderNowCallToActionTitle: ISimpleTranslation[];
  orderNowCallToActionText: ISimpleTranslation[];
  orderNowCallToActionFooterText: ISimpleTranslation[];
  howToCallToAction: ICallToActionComponent[];
  doItYourselfVsProfessionalTitle: ISimpleTranslation[];
  doItYourselfList: IServiceListComponent[];
  professionalList: IServiceListComponent[];
  customerReviews: ICustomerReviewsComponent[];
  faqsTitle: ISimpleTranslation[];
  faqs: IFrequentlyAskedQuestion[];
  contactUsForm: IContactUsForm[];
  topCallToAction?: ICallToActionComponent[];
  bottomCallToAction?: ICallToActionComponent[];
}

export interface IGenericServiceLanding {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  country: ICountry;
  metas: IMetaComponent[];
  heroHeaders: ISimpleTranslation[];
  heroHeadersHighlightedText?: ISimpleTranslation[];
  heroSubTitles: ISimpleTranslation[];
  serviceTitles: ISimpleTranslation[];
  serviceTexts: ISimpleTranslation[];
  bodies: ISimpleTranslation[];
  fromPrice: number;
  faqsTitle?: ISimpleTranslation[];
  faqs?: IFrequentlyAskedQuestion[];
  breadcrumbAncestors?: IBreadcrumb[];
  bottomCallToAction?: ICallToActionComponent[];
  contactUsForm?: IContactUsForm[];
  contactUsFormSuccessMessage?: ISimpleTranslation[];
  contactUsFormErrorMessage?: ISimpleTranslation[];
  howItWorksTitle?: ISimpleTranslation[];
  howItWorks?: ITextBlockWithIcon[];
  introductionTitle?: ISimpleTranslation[];
  introductionDescription?: ISimpleTranslation[];
  introductionImage?: IMedia;
  businessKeyValues?: IBusinessKeyValuesComponent[];
  countryKeyFacts?: ICountryKeyFacts[];
}

export interface IBankWireInformation {
  title: ISimpleTranslation[];
  currencyCode: string;
  bankLabel: ISimpleTranslation[];
  bankCodeLabel: ISimpleTranslation[];
  accountLabel?: ISimpleTranslation[];
  routingLabel?: ISimpleTranslation[];
  ibanLabel?: ISimpleTranslation[];
  ukSortCodeLabel?: ISimpleTranslation[];
  bankContent: string;
  bankCodeContent: string;
  accountContent?: string;
  routingContent?: string;
  ibanContent?: string;
  ukSortCodeContent?: string;
}

export interface ICheckoutPage extends IPage {
  stepOneTitle: ISimpleTranslation[];
  stepTwoTitle: ISimpleTranslation[];
  stepThreeTitle: ISimpleTranslation[];
  countrySelectionTitle: ISimpleTranslation[];
  countrySelectionPlaceholder: ISimpleTranslation[];
  countrySelectionAddButton: ISimpleTranslation[];
  trademarkInformationTitle: ISimpleTranslation[];
  trademarkInformationPlaceholder: ISimpleTranslation[];
  uploadLogoTip: ISimpleTranslation[];
  goodsAndServicesTitle: ISimpleTranslation[];
  goodsAndServicesDescription: ISimpleTranslation[];
  goodsAndServicesNotice: ISimpleTranslation[];
  goodsAndServicesButtonLabel: ISimpleTranslation[];
  goodsAndServicesButtonTip: ISimpleTranslation[];
  goodsAndServicesSearchPlaceholder: ISimpleTranslation[];
  ownerTitle: ISimpleTranslation[];
  ownerDescription: ISimpleTranslation[];
  addressesEmptyPlaceholder: ISimpleTranslation[];
  ownerCompanyLabel: ISimpleTranslation[];
  ownerCompanyPlaceholder: ISimpleTranslation[];
  ownerTaxIdLabel: ISimpleTranslation[];
  ownerTaxIdPlaceholder: ISimpleTranslation[];
  ownerAddressesTitle: ISimpleTranslation[];
  ownerAddAddressButton: ISimpleTranslation[];
  orderSummaryTitle: ISimpleTranslation[];
  billingTitle: ISimpleTranslation[];
  billingCreditCardTabName: ISimpleTranslation[];
  billingBankWireTabName: ISimpleTranslation[];
  billingBankWireNotice: ISimpleTranslation[];
  billingBankWireNoticeLink: ISimpleTranslation[];
  billingBankWireNoticeLinkLabel: ISimpleTranslation[];
  billingBankWireBeneficiaryLabel: ISimpleTranslation[];
  billingBankWireBeneficiary: ISimpleTranslation[];
  billingBankWireAddressLabel: ISimpleTranslation[];
  billingBankWireAddress: ISimpleTranslation[];
  billingBankWireInformation: IBankWireInformation[];
  totalLabel: ISimpleTranslation[];
  backButton: ISimpleTranslation[];
  nextButton: ISimpleTranslation[];
  payButton: ISimpleTranslation[];
  goodsAndServicesClassLabel: ISimpleTranslation[];
  addressSaveSuccessMessage: ISimpleTranslation[];
  addressSaveErrorMessage: ISimpleTranslation[];
  addressDeletePromptMessage: ISimpleTranslation[];
  addressDeleteSuccessMessage: ISimpleTranslation[];
  addressDeleteErrorMessage: ISimpleTranslation[];
  addressEditButton: ISimpleTranslation[];
  addressDeleteButton: ISimpleTranslation[];
  countryRemoveButton: ISimpleTranslation[];
  uploadLogoTitle: ISimpleTranslation[];
  uploadLogoRemoveButton: ISimpleTranslation[];
  goodsAndServicesSelectedTitle: ISimpleTranslation[];
  goodsAndServicesRemoveButton: ISimpleTranslation[];
  goodsAndServicesModalTitle: ISimpleTranslation[];
  goodsAndServicesModalDescription: ISimpleTranslation[];
  goodsAndServicesModalCancelButton: ISimpleTranslation[];
  goodsAndServicesModalDoneButton: ISimpleTranslation[];
  goodsAndServicesClassDescripionPlaceholder: ISimpleTranslation[];
  billingBankWireClipboardTip: ISimpleTranslation[];
  termsCheckboxLabel: ISimpleTranslation[];
  billingPaymentMethodsTitle: ISimpleTranslation[];
  addressBookCreateOrModify: IAddressBookCreateOrModify[];
  noServiceAvailableMessage?: ISimpleTranslation[];
}

export interface ICheckoutBankwireResultPage extends IPage {
  title: ISimpleTranslation[];
  message: ISimpleTranslation[];
  reviewProcess: ICheckoutReviewProcess[];
}

export interface ICheckoutCreditCardResultPage extends IPage {
  indeterminateTitle: ISimpleTranslation[];
  indeterminateMessage: ISimpleTranslation[];
  successTitle: ISimpleTranslation[];
  successMessage: ISimpleTranslation[];
  errorTitle: ISimpleTranslation[];
  errorMessage: ISimpleTranslation[];
  errorButton: ISimpleTranslation[];
  reviewProcess: ICheckoutReviewProcess[];
}

export interface IOAuthConnectResultPage extends IPage {
  successTitle: ISimpleTranslation[];
  successMessage: ISimpleTranslation[];
  errorTitle: ISimpleTranslation[];
  errorMessage: ISimpleTranslation[];
  errorButton: ISimpleTranslation[];
}

export interface ILoginPage extends IPage {
  emailLabel: ISimpleTranslation[];
  passwordLabel: ISimpleTranslation[];
  forgotPasswordButton: ISimpleTranslation[];
  logInButton: ISimpleTranslation[];
  registerTip: ISimpleTranslation[];
  registerButton: ISimpleTranslation[];
  formSubmitErrorMessage: ISimpleTranslation[];
  emailNotValidMessage: ISimpleTranslation[];
  emailRequiredMessage: ISimpleTranslation[];
  passwordRequiredMessage: ISimpleTranslation[];
  alternativeLoginMethodsTitle: ISimpleTranslation[];
}

export interface IForgotPasswordPage extends IPage {
  description: ISimpleTranslation[];
  emailLabel: ISimpleTranslation[];
  submitButton: ISimpleTranslation[];
  emailRequiredMessage: ISimpleTranslation[];
  emailNotValidMessage: ISimpleTranslation[];
  formSubmitSuccessMessage: ISimpleTranslation[];
  formSubmitErrorMessage: ISimpleTranslation[];
}

export interface IRegisterPage extends IPage {
  nameLabel: ISimpleTranslation[];
  emailLabel: ISimpleTranslation[];
  passwordLabel: ISimpleTranslation[];
  confirmPasswordLabel: ISimpleTranslation[];
  newsletterCheckboxLabel: ISimpleTranslation[];
  termsCheckboxLabel: ISimpleTranslation[];
  submitButton: ISimpleTranslation[];
  logInTip: ISimpleTranslation[];
  logInButton: ISimpleTranslation[];
  nameRequiredMessage: ISimpleTranslation[];
  emailNotValidMessage: ISimpleTranslation[];
  emailRequiredMessage: ISimpleTranslation[];
  passwordRequiredMessage: ISimpleTranslation[];
  passwordConfirmationRequiredMessage: ISimpleTranslation[];
  passwordNotMatchMessage: ISimpleTranslation[];
  termsRequiredMessage: ISimpleTranslation[];
  formSubmitSuccessMessage: ISimpleTranslation[];
  formSubmitErrorMessage: ISimpleTranslation[];
}

export interface IResetPasswordPage extends IPage {
  codeLabel: ISimpleTranslation[];
  passwordLabel: ISimpleTranslation[];
  confirmPasswordLabel: ISimpleTranslation[];
  submitButtonLabel: ISimpleTranslation[];
  formSubmitSuccessMessage: ISimpleTranslation[];
  formSubmitErrorMessage: ISimpleTranslation[];
  loginButtonLabel: ISimpleTranslation[];
}

export interface IAccountAddressBookPage extends IPage {
  title: ISimpleTranslation[];
  createNewAddressButton: ISimpleTranslation[];
  emptyTitle: ISimpleTranslation[];
  emptyMessage: ISimpleTranslation[];
  emptyButton: ISimpleTranslation[];
  formSubmitSuccessMessage: ISimpleTranslation[];
  formSubmitErrorMessage: ISimpleTranslation[];
  addressBookCreateOrModify: IAddressBookCreateOrModify[];
}

export interface IAccountOrdersPage extends IPage {
  pendingServicesTitle: ISimpleTranslation[];
  pendingServicesColumnService: ISimpleTranslation[];
  pendingServicesColumnClasses: ISimpleTranslation[];
  pendingServicesColumnCountry: ISimpleTranslation[];
  pendingServicesColumnPrice: ISimpleTranslation[];
  pendingServicesColumnName: ISimpleTranslation[];
  pendingServicesRemoveButton: ISimpleTranslation[];
  pendingServicesEditButton: ISimpleTranslation[];
  pendingServicesBatchPayButton: ISimpleTranslation[];
  paidOrdersTitle: ISimpleTranslation[];
  paidOrdersColumnOrderNumber: ISimpleTranslation[];
  paidOrdersColumnDate: ISimpleTranslation[];
  paidOrdersColumnState: ISimpleTranslation[];
  paidOrdersColumnTotal: ISimpleTranslation[];
  paidOrdersInvoiceButton: ISimpleTranslation[];
  emptyTitle: ISimpleTranslation[];
  emptyMessage: ISimpleTranslation[];
  emptyButton: ISimpleTranslation[];
  serviceNameTrademarkApplication: ISimpleTranslation[];
  serviceNameTrademarkSearch: ISimpleTranslation[];
  serviceNameAdditional: ISimpleTranslation[];
}

export interface IAccountPortfolioPage extends IPage {
  portfolioIncludeInactiveLabel?: ISimpleTranslation[];
  designsLabel?: ISimpleTranslation[];
  copyrightsLabel?: ISimpleTranslation[];
  designsColumnName?: ISimpleTranslation[];
  copyrightsColumnName?: ISimpleTranslation[];
  designsEmptyTitle?: ISimpleTranslation[];
  designsEmptyMessage?: ISimpleTranslation[];
  designsEmptyButton?: ISimpleTranslation[];
  copyrightsEmptyTitle?: ISimpleTranslation[];
  copyrightsEmptyMessage?: ISimpleTranslation[];
  copyrightsEmptyButton?: ISimpleTranslation[];
  trackerDescriptionLabel?: ISimpleTranslation[];
  portfolioSearchPlaceholder?: ISimpleTranslation[];
  portfolioNoResultsMessage?: ISimpleTranslation[];
  portfolioLoadingLabel?: ISimpleTranslation[];
  portfolioErrorMessage?: ISimpleTranslation[];
  portfolioRetryButton?: ISimpleTranslation[];
  portfolioCloseButton?: ISimpleTranslation[];
  portfolioDownloadErrorMessage?: ISimpleTranslation[];
  title: ISimpleTranslation[];
  trademarksLabel: ISimpleTranslation[];
  trademarksSearchPlaceholder: ISimpleTranslation[];
  trademarksColumnName: ISimpleTranslation[];
  trademarksColumnLogo: ISimpleTranslation[];
  trademarksColumnCountry: ISimpleTranslation[];
  trademarksColumnStatus: ISimpleTranslation[];
  trademarksColumnRenewalDate: ISimpleTranslation[];
  trademarksOpenButton: ISimpleTranslation[];
  trademarksEmtpyTitle: ISimpleTranslation[];
  trademarksEmtpyMessage: ISimpleTranslation[];
  trademarksEmtpyButton: ISimpleTranslation[];
  trademarksFilterAllCountries: ISimpleTranslation[];
  trademarksFilterAllStatus: ISimpleTranslation[];
  trademarksExportButton: ISimpleTranslation[];
  searchesLabel: ISimpleTranslation[];
  searchesSearchPlaceholder: ISimpleTranslation[];
  searchesColumnName: ISimpleTranslation[];
  searchesColumnLogo: ISimpleTranslation[];
  searchesColumnCountry: ISimpleTranslation[];
  searchesColumnStatus: ISimpleTranslation[];
  searchesReportButton: ISimpleTranslation[];
  searchesEmptyTitle: ISimpleTranslation[];
  searchesEmptyMessage: ISimpleTranslation[];
  searchesEmptyButton: ISimpleTranslation[];
  editorTitle: ISimpleTranslation[];
  editorDetailsTitle: ISimpleTranslation[];
  editorDetailsTrademarkLabel: ISimpleTranslation[];
  editorDetailsLogoLabel: ISimpleTranslation[];
  editorDetailsClassesLabel: ISimpleTranslation[];
  editorDetailsOwnerNameLabel: ISimpleTranslation[];
  editorDetailsOwnerAddressLabel: ISimpleTranslation[];
  editorDetailsStatusLabel: ISimpleTranslation[];
  editorDetailsFillingDateLabel: ISimpleTranslation[];
  editorDetailsFillingNumberLabel: ISimpleTranslation[];
  editorDetailsRegistrationNumberLabel: ISimpleTranslation[];
  editorDetailsRenewalDateLabel: ISimpleTranslation[];
  editorFilesTitle: ISimpleTranslation[];
  editorFilesDownloadButton: ISimpleTranslation[];
  editorDetailsCountryLabel: ISimpleTranslation[];
  editorDetailsRegistrationDateLabel: ISimpleTranslation[];
}

export interface IAccountProfilePage extends IPage {
  myAccountTitle: ISimpleTranslation[];
  myAccountNameLabel: ISimpleTranslation[];
  myAccountEmailLabel: ISimpleTranslation[];
  myAccountPreferredLanguageLabel: ISimpleTranslation[];
  myAccountPreferredCurrencyLabel: ISimpleTranslation[];
  myAccountConsultantLabel: ISimpleTranslation[];
  myAccountConsultantNameLabel: ISimpleTranslation[];
  myAccountConsultantEmailLabel: ISimpleTranslation[];
  myAccountPhoneNumberLabel: ISimpleTranslation[];
  myAccountNewsletterLabel: ISimpleTranslation[];
  myAccountSubmitButton: ISimpleTranslation[];
  changePasswordTitle: ISimpleTranslation[];
  changePasswordOldPasswordLabel: ISimpleTranslation[];
  changePasswordNewPasswordLabel: ISimpleTranslation[];
  changePasswordNewPasswordConfirmLabel: ISimpleTranslation[];
  changePasswordSubmitButton: ISimpleTranslation[];
  myAccountFormSubmitSuccessMessage: ISimpleTranslation[];
  myAccountFormErrorSuccessMessage: ISimpleTranslation[];
  changePasswordFormSubmitSuccessMessage: ISimpleTranslation[];
  changePasswordErrorSuccessMessage: ISimpleTranslation[];
  validationNameIsRequired: ISimpleTranslation[];
  validationEmailIsRequired: ISimpleTranslation[];
  validationEmailIsNotValid: ISimpleTranslation[];
  validationPhoneIsNotValid: ISimpleTranslation[];
  validationOldPasswordIsRequired: ISimpleTranslation[];
  validationNewPasswordIsRequired: ISimpleTranslation[];
  validationPasswordMinLength: ISimpleTranslation[];
  validationConfirmPasswordIsRequired: ISimpleTranslation[];
  validationPasswordNotMatch: ISimpleTranslation[];
}

export interface IServiceWithTopicsPage extends IPage {
  heroTipsTitle: ISimpleTranslation[];
  heroTipsSubTitle: ISimpleTranslation[];
  heroTipsDescription: ISimpleTranslation[];
  heroTipOne: ISimpleTranslation[];
  heroTipTwo: ISimpleTranslation[];
  heroTipThree: ISimpleTranslation[];
  heroTipFour: ISimpleTranslation[];
  heroImage: IMedia;
  introductionTitle: ISimpleTranslation[];
  introductionDescription: ISimpleTranslation[];
  introductionImage: IMedia;
  topics: IPageContentBlockWithTitleComponent[];
  countryListCallToAction: IHeroComponent[];
  businessKeyValues: IBusinessKeyValuesComponent[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage: ISimpleTranslation[];
  contactUsFormErrorMessage: ISimpleTranslation[];
}

export interface IMadridSystemPage extends IServiceWithTopicsPage {}

export interface IInternationalTrademarkRegistrationPage
  extends IServiceWithTopicsPage {}

export interface IFaqsPage extends IPage {
  hero: IHeroComponent[];
  categoriesTitle: ISimpleTranslation[];
  faqCategories: IFaqCategory[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage: ISimpleTranslation[];
  contactUsFormErrorMessage: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
}

export interface IFaqLandingPage {
  metas: IMetaComponent[];
  faqCategory?: IFaqCategory;
  image?: IImageTranslation[];
  title: ISimpleTranslation[];
  summary: ISimpleTranslation[];
  body: ISimpleTranslation[];
  expertTip: IExpertTipComponent[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage: ISimpleTranslation[];
  contactUsFormErrorMessage: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  breadcrumbAncestors: IBreadcrumb[];
  author?: ITeamMemberPage;
  lastUpdatedLabel: ISimpleTranslation[];
  updatedAt?: Date;
  tableOfContentsTitle: ISimpleTranslation[];
}

export interface IFaqSubCategoryLandingPage {
  metas: IMetaComponent[];
  hero: IHeroComponent[];
  categoriesTitle: ISimpleTranslation[];
  faqCategories: IFaqCategory[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage: ISimpleTranslation[];
  contactUsFormErrorMessage: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  breadcrumbAncestors?: IBreadcrumb[];
}

export interface ITestimonialsPage extends IPage {
  hero: IHeroComponent[];
  trustedBy: ITrustedByComponent[];
  customerReviewsTitle: ISimpleTranslation[];
  reviews: ICustomerReview[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage: ISimpleTranslation[];
  contactUsFormErrorMessage: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  callToActionWithImage: ICallToActionComponent[];
  testimonialTips: ITestimonialTip[];
  breadcrumbAncestors?: IBreadcrumb[];
  metas: IMetaComponent[];
  externalLinkButtonOne: ILinkComponent[];
  externalLinkButtonTwo: ILinkComponent[];
}

export interface INiceClassificatorPage extends IPage {
  title: ISimpleTranslation[];
  subTitle: ISimpleTranslation[];
  promptPlaceholder: ISimpleTranslation[];
  promptButtonLabel: ISimpleTranslation[];
  resultTitle: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  disclaimer: ISimpleTranslation[];
  maximumNumberOfRequestsMessage: ISimpleTranslation[];
}

export interface ISemTrademarkRenewalPage extends IPage {
  hero: IHeroComponent[];
  introductionTitle: ISimpleTranslation[];
  introductionDescription: ISimpleTranslation[];
  introductionImage: IMedia;
  businessKeyValues: IBusinessKeyValuesComponent[];
  customerReviews: ICustomerReviewsComponent[];
  trustedBy: ITrustedByComponent[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage?: ISimpleTranslation[];
  contactUsFormErrorMessage?: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  breadcrumbAncestors?: IBreadcrumb[];
}

export interface IGenericLandingPage extends IPage {
  hero: IHeroComponent[];
  testimonialTips: ITestimonialTip[];
  introductionTitle: ISimpleTranslation[];
  introductionDescription: ISimpleTranslation[];
  introductionImage: IMedia;
  topics: IPageContentBlockWithTitleComponent[];
  businessKeyValues: IBusinessKeyValuesComponent[];
  customerReviews: ICustomerReviewsComponent[];
  trustedBy: ITrustedByComponent[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage?: ISimpleTranslation[];
  contactUsFormErrorMessage?: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  breadcrumbAncestors?: IBreadcrumb[];
}

export interface ITrademarkServicesPage extends IPage {
  hero: IHeroComponent[];
  featuredServices: IHomeFeaturedServicesComponent[];
  countryListCallToAction: IHeroComponent[];
  introductionTitle: ISimpleTranslation[];
  introductionDescription: ISimpleTranslation[];
  introductionImage: IMedia;
  businessKeyValues: IBusinessKeyValuesComponent[];
  customerReviews: ICustomerReviewsComponent[];
  trustedBy: ITrustedByComponent[];
  faqsTitle: ISimpleTranslation[];
  faqs: IFrequentlyAskedQuestion[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage?: ISimpleTranslation[];
  contactUsFormErrorMessage?: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  breadcrumbAncestors?: IBreadcrumb[];
}

export interface ITrademarkServicesPage extends IPage {
  hero: IHeroComponent[];
  introductionTitle: ISimpleTranslation[];
  introductionDescription: ISimpleTranslation[];
  introductionImage: IMedia;
  quickAccessTitle: ISimpleTranslation[];
  quickAccessSubTitle: ISimpleTranslation[];
  quickAccessShowAllButton: ISimpleTranslation[];
  quickAccessShowAllButtonPath: ISimpleTranslation[];
  businessKeyValues: IBusinessKeyValuesComponent[];
  howItWorksTitle: ISimpleTranslation[];
  howItWorks: ITextBlockWithIcon[];
  trustedBy: ITrustedByComponent[];
  customerReviews: ICustomerReviewsComponent[];
  faqsTitle: ISimpleTranslation[];
  faqs: IFrequentlyAskedQuestion[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage?: ISimpleTranslation[];
  contactUsFormErrorMessage?: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  breadcrumbAncestors?: IBreadcrumb[];
}

export interface IDeclarationOfUseServicesPage extends IPage {
  hero: IHeroComponent[];
  introductionTitle: ISimpleTranslation[];
  introductionDescription: ISimpleTranslation[];
  introductionImage: IMedia;
  quickAccessTitle: ISimpleTranslation[];
  quickAccessSubTitle: ISimpleTranslation[];
  quickAccessShowAllButton: ISimpleTranslation[];
  quickAccessShowAllButtonPath: ISimpleTranslation[];
  businessKeyValues: IBusinessKeyValuesComponent[];
  howItWorksTitle: ISimpleTranslation[];
  howItWorks: ITextBlockWithIcon[];
  trustedBy: ITrustedByComponent[];
  customerReviews: ICustomerReviewsComponent[];
  faqsTitle: ISimpleTranslation[];
  faqs: IFrequentlyAskedQuestion[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage?: ISimpleTranslation[];
  contactUsFormErrorMessage?: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  breadcrumbAncestors?: IBreadcrumb[];
}

export interface ITrademarkMonitoringPage extends IPage {
  hero: IHeroComponent[];
  introductionTitle: ISimpleTranslation[];
  introductionDescription: ISimpleTranslation[];
  introductionImage: IMedia;
  businessKeyValues: IBusinessKeyValuesComponent[];
  howItWorksTitle: ISimpleTranslation[];
  howItWorks: ITextBlockWithIcon[];
  trustedBy: ITrustedByComponent[];
  customerReviews: ICustomerReviewsComponent[];
  faqsTitle: ISimpleTranslation[];
  faqs: IFrequentlyAskedQuestion[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage?: ISimpleTranslation[];
  contactUsFormErrorMessage?: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  breadcrumbAncestors?: IBreadcrumb[];
}

export interface ITrademarkObjectionsLandingPage extends IPage {
  hero: IHeroComponent[];
  howToCallToAction: ICallToActionComponent[];
  howItWorksTitle: ISimpleTranslation[];
  howItWorks: ITextBlockWithIcon[];
  businessKeyValues: IBusinessKeyValuesComponent[];
  customerReviews: ICustomerReviewsComponent[];
  trustedBy: ITrustedByComponent[];
  faqsTitle: ISimpleTranslation[];
  faqs: IFrequentlyAskedQuestion[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage?: ISimpleTranslation[];
  contactUsFormErrorMessage?: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  breadcrumbAncestors?: IBreadcrumb[];
}

export interface ITrademarkOppositionsLandingPage extends IPage {
  hero: IHeroComponent[];
  howToCallToAction: ICallToActionComponent[];
  howItWorksTitle: ISimpleTranslation[];
  howItWorks: ITextBlockWithIcon[];
  businessKeyValues: IBusinessKeyValuesComponent[];
  customerReviews: ICustomerReviewsComponent[];
  trustedBy: ITrustedByComponent[];
  faqsTitle: ISimpleTranslation[];
  faqs: IFrequentlyAskedQuestion[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage?: ISimpleTranslation[];
  contactUsFormErrorMessage?: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  breadcrumbAncestors?: IBreadcrumb[];
}

export interface IDomainNameDisputesLandingPage extends IPage {
  hero: IHeroComponent[];
  introductionTitle: ISimpleTranslation[];
  introductionDescription: ISimpleTranslation[];
  topics: IPageContentBlockWithTitleComponent[];
  howToCallToAction: ICallToActionComponent[];
  howItWorksTitle: ISimpleTranslation[];
  howItWorks: ITextBlockWithIcon[];
  businessKeyValues: IBusinessKeyValuesComponent[];
  trustedBy: ITrustedByComponent[];
  customerReviews: ICustomerReviewsComponent[];
  faqsTitle: ISimpleTranslation[];
  faqs: IFrequentlyAskedQuestion[];
  contactUsForm: IContactUsForm[];
  contactUsFormSuccessMessage?: ISimpleTranslation[];
  contactUsFormErrorMessage?: ISimpleTranslation[];
  callToAction: ICallToActionComponent[];
  breadcrumbAncestors?: IBreadcrumb[];
}

export interface ILinkComponent {
  language: string;
  link: string;
  label: string;
}

export interface ILinkWithIconComponent {
  link: string;
  icon: IMedia;
  label?: string;
}

export interface ITeamMemberSkillItemComponent {
  name: string;
}

export interface ITeamMemberSkillsComponent {
  language: string;
  skills: ITeamMemberSkillItemComponent[];
}
export interface ITeamMemberPage extends IPage {
  id?: number;
  avatar: IMedia;
  fullname: string;
  jobTitle: ISimpleTranslation[];
  socialMediaIcons: ILinkWithIconComponent[];
  biography: ISimpleTranslation[];
  education: ITextBlockWithIcon[];
  expertise: ITextBlockWithIcon[];
  expertiseSkills: ITeamMemberSkillsComponent[];
  publishedArticles: ISimpleTranslation[];
  latestPostsTitle: ISimpleTranslation[];
  otherMembersTitle: ISimpleTranslation[];
  otherMembersSubTitle: ISimpleTranslation[];
  blogPosts: IBlogPost[];
}

/* Pages  ------------------------------------------------------------- */

/* SEO Components  ---------------------------------------------------- */
export interface IMetaComponent {
  title: string;
  description: string;
  slug: string;
  language: string;
  keywords?: string;
  noindex?: boolean;
}

/* SEO Components  ---------------------------------------------------- */

/* Common  ------------------------------------------------------------ */

export interface ICommonTranslations {
  serviceTypeSearch: ISimpleTranslation[];
  serviceTypeApplication: ISimpleTranslation[];
  serviceTypeAdditionalService: ISimpleTranslation[];
  serviceTypeIndustrialDesign: ISimpleTranslation[];
  serviceTypeCopyright: ISimpleTranslation[];
  requiredField: ISimpleTranslation[];
  passwordMinimumLength: ISimpleTranslation[];
  passwordsMustMatch: ISimpleTranslation[];
  emailFormatNotValid: ISimpleTranslation[];
  termsAndConditionsMustBeAccepted: ISimpleTranslation[];
  showMore: ISimpleTranslation[];
  showLess: ISimpleTranslation[];
  feesIncluded: ISimpleTranslation[];
  faqsTitle: ISimpleTranslation[];
  homePageName: ISimpleTranslation[];
  organizationDescription: ISimpleTranslation[];
  contactUsFormSuccessMessage: ISimpleTranslation[];
  contactUsFormErrorMessage: ISimpleTranslation[];
  lastUpdatedLabel: ISimpleTranslation[];
  tableOfContentsTitle: ISimpleTranslation[];
  // featuredServices: IHomeFeaturedServicesComponent[];
  featuredServicesCallToAction: ICallToActionComponent[];
  customerReviewsTopTipStart: ISimpleTranslation[];
  customerReviewsTopTipEnd: ISimpleTranslation[];
  customerReviewsBottomTip: ISimpleTranslation[];
  trustFactsStrip: ITrustFacts[];
}

export interface IEntitiesStatus {
  trackerInProcess: ISimpleTranslation[];
  trackerRegistered: ISimpleTranslation[];
  trackerFiled: ISimpleTranslation[];
  trackerCompleted: ISimpleTranslation[];
  trackerInactive: ISimpleTranslation[];
  trackerAbandoned: ISimpleTranslation[];
  trackerRefused: ISimpleTranslation[];
  trackerWaitingForPoa: ISimpleTranslation[];
  trackerUK00918095923: ISimpleTranslation[];
  paymentPending: ISimpleTranslation[];
  paymentDone: ISimpleTranslation;
  orderCompleted: ISimpleTranslation[];
  orderCanceled: ISimpleTranslation[];
  orderDraft: ISimpleTranslation[];
  orderPending: ISimpleTranslation[];
  orderPaidAndPendingTrackersCreation: ISimpleTranslation[];
}

export interface IAddressBookCreateOrModify {
  language: string;
  aliasLabel?: string;
  aliasPlaceholder: string;
  nameLabel?: string;
  namePlaceholder: string;
  addressLabel?: string;
  addressPlaceholder: string;
  postCodeLabel?: string;
  postCodePlaceholder: string;
  cityLabel?: string;
  cityPlaceholder: string;
  countryLabel?: string;
  countryPlaceholder: string;
  referenceSectionTitle: string;
  nameSectionTitle: string;
  addressSectionTitle: string;
  taxIdLabel?: string;
  taxIdPlaceholder?: string;
  title: string;
  saveButton: string;
  deleteButton: string;
}

/* Common  ------------------------------------------------------------ */

/* Layout and UI ------------------------------------------------------ */
export interface IBasePage {
  pageSchema: PageJSONLDSchema;
}

export interface ILayoutPageProps extends IBasePage {
  locale: string;
  meta: IMetaComponent;
  navBarProps: INavBarProps;
  cookiesPopUpProps: ICookiesPopupProps;
  footerProps: IFooterProps;
}

export interface INavBarItem {
  label: string;
  path: string;
}

export interface IMainNavBar {
  servicesLabel: string;
  trademarkServicesGroupLabel: string;
  trademarkServicesGroupPath: string;
  trademarkServicesGroupItems: INavBarItem[];
  otherIpServicesGroupLabel: string;
  otherIpServicesGroupItems: INavBarItem[];
  pricingLabel: string;
  pricingItems: INavBarItem[];
  resourcesLabel: string;
  intellectualPropertyQuestionGroupLabel: string;
  intellectualPropertyQuestionGroupPath: string;
  intellectualPropertyQuestionGroupItems: INavBarItem[];
  otherResourcesGroupLabel: string;
  otherResourcesGroupItems: INavBarItem[];
  aboutUsLabel: string;
  aboutUsItems: INavBarItem[];
  loginLabel: string;
  logoutLabel: string;
  accountProfileLabel: string;
  contactUsLabel: string;
  ordersLabel: string;
  portfolioLabel: string;
  addressBookLabel: string;
}

export interface IMainFooter {
  servicesLabel: string;
  servicesItems: INavBarItem[];
  contactLabel: string;
  paymentLabel: string;
  copyright: string;
  disclaimer: string;
  cookiesPolicyItem: INavBarItem;
  privacyItem: INavBarItem;
  termsItem: INavBarItem;
  blogPostsLabel: string;
  blogPosts: IBlogPost[];
}

export interface ICookiesPopup {
  message: string;
  secondaryButtonLabel: string;
  primaryButtonLabel: string;
}

export interface IHeroComponent {
  title: string;
  description: string;
  buttonLabel?: string;
  buttonPath?: string;
  secondaryButtonLabel?: string;
  secondaryButtonPath?: string;
  background?: IMedia | null | undefined;
  language: string;
  highlightedTitleText?: string | undefined;
}

export interface IHomeFeaturedServicesComponent {
  language: string;
  title: string;
  description: string;
  serviceOneTitle: string;
  serviceOneDescription: string;
  serviceOneIcon: IMedia;
  serviceOnePath: string;
  serviceTwoTitle: string;
  serviceTwoDescription: string;
  serviceTwoIcon: IMedia;
  serviceTwoPath: string;
  serviceThreeTitle: string;
  serviceThreeDescription: string;
  serviceThreeIcon: IMedia;
  serviceThreePath: string;
}

export interface ITrustedByComponent {
  language: string;
  title: string;
  logos: IMedia[];
}

export interface IContactUsCallToActionComponent {
  language: string;
  title: string;
  description: string;
  buttonLabel: string;
  image: IMedia;
}

export interface IBusinessKeyValuesComponent {
  language: string;
  title: string;
  description: string;
  keyValueOneTitle: string;
  keyValueOneDescription: string;
  keyValueTwoTitle: string;
  keyValueTwoDescription: string;
  keyValueThreeTitle: string;
  keyValueThreeDescription: string;
  keyValueFourTitle: string;
  keyValueFourDescription: string;
}

export interface ICustomerReviewsComponent {
  language: string;
  title: string;
  description: string;
  buttonLabel?: string;
  buttonPath?: string;
}

export interface IAccountLeftNavBar {
  designsLabel?: ISimpleTranslation[];
  copyrightsLabel?: ISimpleTranslation[];
  profileLabel: ISimpleTranslation[];
  ordersLabel: ISimpleTranslation[];
  portfolioLabel: ISimpleTranslation[];
  trademarksLabel: ISimpleTranslation[];
  searchesLabel: ISimpleTranslation[];
  addressBookLabel: ISimpleTranslation[];
}

export interface IContactUsForm {
  language: string;
  title: string;
  subTitle: string;
  emailLabel: string;
  nameLabel: string;
  messageLabel: string;
  submitLabel: string;
}

export interface IHighlightedTitleComponent {
  language: string;
  title: string;
  subTitle: string;
  highlightedText?: string;
}

export interface IServiceListComponent {
  language: string;
  title: string;
  items: string;
}

export interface IPageContentBlockWithTitleComponent {
  language: string;
  title: string;
  text: string;
}

export interface ICountryRelatedService {
  title: string;
  description: string;
  slug: string;
}

export interface IExpertTipComponent {
  language: string;
  title: string;
  content: string;
  primaryButtonLabel: string;
  secondaryButtonLabel?: string;
  secondaryButtonPath?: string;
}

export interface ICallToActionButtonComponent {
  label: string;
  path: string;
  action?: () => void;
}

export interface ICallToActionComponent {
  language: string;
  title: string;
  content?: string;
  buttons: ICallToActionButtonComponent[];
  image?: IMedia;
}

export interface IFaqCategory {
  id: number;
  name: ISimpleTranslation[];
  subCategoryLandingPath: ISimpleTranslation[];
  subCategoryLandingButtonLabel: ISimpleTranslation[];
  faqLandings: IFaqLandingPage[];
  icon?: IMedia;
}

export interface ITestimonialTip {
  title: ISimpleTranslation[];
  subTitle: ISimpleTranslation[];
  icon: IMedia;
}

/* Layout and UI ------------------------------------------------------ */

export interface ICustomerReviewsMeta {
  average: number;
  numberOfReviews: number;
  bestRating: number;
  worstRating: number;
}

export interface ITextBlockWithIcon {
  language: string;
  content: string;
  icon: IMedia;
}

export interface IUserPathEntryComponent {
  url: string;
  title: string;
  timestamp: Date;
  locale: string;
  device: 'mobile' | 'tablet' | 'desktop';
  referrer?: string;
}

export interface ICheckoutReviewProcess {
  ratingTitle: string;
  ratingMessage: string;
  ratingRequiredMessage: string;
  accountButtonLabel: string;
  nextButtonLabel: string;
  reviewModalTitle: string;
  reviewModalMessage: string;
  reviewModalContentPlacheholder: string;
  reviewModalChangeRatingButtonLabel: string;
  reviewModalOmitButtonLabel: string;
  reviewModalSendButtonLabel: string;
  reviewModalSentSuccessfullyLabel: string;
  shareModalTitle: string;
  shareModalMessage: string;
  shareModalTip: string;
  shareModalCloseButtonLabel: string;
  shareModalExternalLinkButtons: ILinkWithIconComponent[];
}

export interface IKeyFact {
  icon: IMedia;
  title: string;
  text: string;
  tip?: string;
  anchor?: string;
}

export interface ICountryKeyFacts {
  language: string;
  title: string;
  facts: IKeyFact[];
}

export interface ITrustFact {
  value: number;
  description: string;
}
export interface ITrustFacts {
  language: string;
  facts: ITrustFact[];
}

export interface IContactSubmissionResult {
  ok: boolean;
  iss: boolean;
  data?: any;
}

export interface INewsletterUnsubscribePage extends IPage {
  callToAction: ICallToActionComponent[];
  formTitle: ISimpleTranslation[];
  formSubtitle: ISimpleTranslation[];
  formSubmitLabel: ISimpleTranslation[];
  formSubmitSuccessMessage: ISimpleTranslation[];
  formSubmitErrorMessage: ISimpleTranslation[];
}
